import { InvitationResponseEnum, ReplyRequest } from "api";
import Form from "components/Form";
import { Button, Container, Flex, Well } from "components/UI";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApi } from "services/api";
import { formatDate } from "utility/functions";
import { useValidationErrors } from "utility/hooks";
const QUERY_KEY = 'getReplyInfo';
type FormType = { accomodation_request?: string | undefined };

export default function Reply() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { id } = useParams();
    const [showRejectionEditor, setShowRejectionEditor] = useState(false);

    let [searchParams] = useSearchParams();
    if (!searchParams.has('signature_hash')) {
        navigate('error');
    }

    let signature = searchParams.get('signature_hash');
    if (signature === '' || signature === null) {
        navigate('error');
    }

    const { invitations } = useApi();
    const { data: response, isLoading } = useQuery([QUERY_KEY], () => invitations[QUERY_KEY]({
        invitationId: Number(id),
        signature_hash: signature ?? '',
    }), {
        enabled: Boolean(id) && Boolean(signature)
    });

    const mutationReply = useMutation((replyRequest: ReplyRequest) => invitations.invitationReply({
        invitationId: Number(id),
        ReplyRequest: replyRequest,
    }));
    const defaultValues: FormType = {
        accomodation_request: '',
    };
    const [formState, setFormState] = useState(defaultValues);

    const [errors, onError, clearErrors] = useValidationErrors();

    const [interestedInFutureEditions, setInterestedInFutureEditions] = useState(false);

    function onSubmitReply(invitationResponse: InvitationResponseEnum, note: string) {
        const request: ReplyRequest = {
            signature_hash: signature ?? '',
            response: invitationResponse,
            accomodation_request: formState.accomodation_request === '1',
            invitationable_note: note
        };
        mutationReply.mutate(request, {
            onSuccess: () => {
                queryClient.invalidateQueries(QUERY_KEY);
            }
        });
    }

    function singledate() {
        if (!response) return;
        return formatDate(response.data.starts_at) !== formatDate(response.data.ends_at) ? false : true;
    }

    const isSingleDate = singledate();

    function formatEventDate(startsAt: string, endsAt: string) {
        const start = formatDate(startsAt);
        const end = formatDate(endsAt);
        return start === end ? `il ${start}` : `dal ${start} al ${end}`;
    }

    return (
        <>
            <Container>
            <Well vertical className="p-4">
                <Well.Title>
                    {isSingleDate
                        ? `Invito al Fideuram Campus Milano il ${formatDate(response?.data.starts_at as string)}`
                        : `Invito al Fideuram Campus Milano dal ${formatDate(response?.data.starts_at as string)} al ${formatDate(response?.data.ends_at as string)}`}
                </Well.Title>

                <div className="flex flex-col gap-1 text-sm">
                    <span>
                        <strong>Partecipante:</strong> {response?.data.participant}
                    </span>

                    {response?.data.events?.length === 1 ? (
                        <span>
                            <strong>Evento:</strong> {response.data.events[0].name}{' '}
                            {formatEventDate(response.data.events[0].starts_at, response.data.events[0].ends_at)}
                        </span>
                    ) : (
                        <div>
                            <strong>Eventi:</strong>
                            <ul className="list-disc list-inside mt-1 ml-2 space-y-1 text-gray-700">
                                {response?.data?.events?.map((course: any, index: number) => (
                                    <li key={index}>
                                        {course.name}{' '}
                                        {formatEventDate(course.starts_at, course.ends_at)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </Well>

                {response && !response.data.already_replied && (
                    <Flex className='justify-center p-10'>
                        <div className='w-full max-w-md flex flex-col items-center'>
                            <Form key={showRejectionEditor ? 'rejected' : 'normal'} onChange={setFormState} defaultValues={defaultValues} errors={errors} className='w-full'>
                            {!isSingleDate && (
                                <Form.Select
                                    label='Pernottamento'
                                    name='accomodation_request'
                                    className={`w-full ${showRejectionEditor ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={showRejectionEditor} // disabilitata se NON ACCETTO
                                    defaultValue='' // opzionale: valore iniziale vuoto
                                >
                                    <option value="1">Voglio pernottare</option>
                                    <option value="0">Non voglio pernottare</option>
                                </Form.Select>
                                )}

                                <Flex className='space-x-4 mt-6 justify-center w-full'>
                                    <Button
                                        isLoading={mutationReply.isLoading}
                                        disabled={showRejectionEditor || (!isSingleDate && formState.accomodation_request === '')}
                                        onClick={() => onSubmitReply(InvitationResponseEnum.Accepted, '')}
                                    >
                                        ACCETTO
                                    </Button>
                                    <Button
                                        
                                        isLoading={mutationReply.isLoading}
                                        onClick={() => {
                                            // reset visivo e disabilita la select
                                            setFormState(prev => ({ ...prev, accomodation_request: undefined }));
                                            setShowRejectionEditor(true);
                                        }}
                                        variant='secondary'
                                        disabled={showRejectionEditor}
                                        >
                                        NON ACCETTO
                                        </Button>
                                </Flex>

                            </Form>

                            {showRejectionEditor && (
                                <div className='mt-6 w-full'>
                                    <label
                                        className={`cursor-pointer p-4 border rounded-md flex items-center gap-3 w-full transition-shadow
                                            ${interestedInFutureEditions ? 'border-blue-600 shadow-md bg-blue-50' : 'border-gray-300 hover:shadow-sm'}`}
                                    >
                                        <input
                                            type='checkbox'
                                            checked={interestedInFutureEditions}
                                            onChange={(e) => setInterestedInFutureEditions(e.target.checked)}
                                            className='w-5 h-5 accent-blue-600'
                                        />
                                        <span className='text-sm font-medium'>
                                            Sono comunque {response?.data.gender === 'f' ? 'interessata' : 'interessato'} a partecipare ad edizioni future
                                        </span>
                                    </label>

                                    <Flex className='space-x-4 mt-6 justify-center w-full'>
                                        <Button
                                            onClick={() => {
                                                setShowRejectionEditor(false);
                                                setInterestedInFutureEditions(false);
                                            }}
                                            variant='tertiary'
                                        >
                                            Annulla
                                        </Button>
                                        <Button
                                            onClick={() =>
                                                onSubmitReply(
                                                    InvitationResponseEnum.Rejected,
                                                    interestedInFutureEditions
                                                        ? `Sono comunque ${response?.data.gender === 'f' ? 'interessata' : 'interessato'} a partecipare ad edizioni future`
                                                        : ''
                                                )
                                            }
                                            variant='secondary'
                                        >
                                            Conferma rifiuto
                                        </Button>
                                    </Flex>
                                </div>
                            )}
                        </div>
                    </Flex>
                )}
                {response && response.data.already_replied && (
                    <Flex className="justify-center mt-4 text-3xl leading-relaxed tracking-widest py-16">
                        <span>{response.data.already_replied}</span>
                    </Flex>
                )}

            </Container>
        </>
    );
}
